import React, { useEffect, useRef, useState } from 'react';
import { Participant, Track } from 'livekit-client';
import { AudioTrack, VideoTrack } from 'livekit-client';
import { Mic, MicOff, Video, VideoOff, User, Maximize2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { useMeetingStore } from '../stores/meetingStore';
import MicIndicator from './MicIndicator';

interface ParticipantTileProps {
  participant: Participant;
  isLocal?: boolean;
  isFocused?: boolean;
  onEnlarge?: () => void;
}

const ParticipantTile: React.FC<ParticipantTileProps> = ({ participant, isLocal = false, isFocused = false, onEnlarge }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Manage camera and screen share tracks separately
  const [cameraTrack, setCameraTrack] = useState<VideoTrack | null>(null);
  const [screenShareTrack, setScreenShareTrack] = useState<VideoTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<AudioTrack | null>(null);

  // Get mirror setting, facing mode, and audio output device from store
  const isMirrored = useMeetingStore((state) => state.isMirrored);
  const facingMode = useMeetingStore((state) => state.facingMode);
  const selectedAudioOutput = useMeetingStore((state) => state.selectedAudioOutput);

  // Force re-render when media states change
  const [, forceUpdate] = useState({});
  const triggerRerender = () => forceUpdate({});

  // Track speaking state separately to ensure UI updates
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    const handleTrack = (track: Track) => {
      console.log(`Handling ${track.kind} track for ${isLocal ? 'local' : 'remote'} participant:`, participant.identity);

      if (track.kind === Track.Kind.Video) {
        const vTrack = track as VideoTrack;
        if (track.source === Track.Source.ScreenShare) {
          setScreenShareTrack(vTrack);
          console.log('Screen share track detected and stored');
        } else {
          setCameraTrack(vTrack);
          console.log('Camera video track detected and stored');
        }
      } else if (track.kind === Track.Kind.Audio) {
        const audioTrack = track as AudioTrack;
        setAudioTrack(audioTrack);

        setTimeout(() => {
          if (audioRef.current && !isLocal) {
            // Don't play local audio to avoid feedback
            track.attach(audioRef.current);
            console.log('Audio track attached to element');
          }
        }, 0);
      }
    };

    const handleTrackDetach = (track: Track) => {
      console.log(`Detaching ${track.kind} track for ${isLocal ? 'local' : 'remote'} participant:`, participant.identity);
      track.detach();
      if (track.kind === Track.Kind.Video) {
        if (track.source === Track.Source.ScreenShare) {
          setScreenShareTrack(null);
        } else {
          setCameraTrack(null);
        }
      } else if (track.kind === Track.Kind.Audio) {
        setAudioTrack(null);
      }
    };

    // Clear existing tracks first
    setCameraTrack(null);
    setScreenShareTrack(null);
    setAudioTrack(null);

    // Handle existing tracks
    participant.videoTrackPublications.forEach((publication) => {
      if (publication.track) {
        handleTrack(publication.track);
      }
    });

    participant.audioTrackPublications.forEach((publication) => {
      if (publication.track) {
        handleTrack(publication.track);
      }
    });

    // Create event handlers with proper cleanup references
    const handlePublished = (publication: any) => {
      if (publication.track) {
        handleTrack(publication.track);
      }
    };

    const handleUnpublished = (publication: any) => {
      if (publication.track) {
        handleTrackDetach(publication.track);
      }
    };

    if (isLocal) {
      // For local participant, listen to localTrackPublished/localTrackUnpublished
      // These events are emitted by LocalParticipant when local media tracks are created/removed
      participant.on('localTrackPublished', handlePublished);
      participant.on('localTrackUnpublished', handleUnpublished);

      // Listen for track mute/unmute events to update icons
      participant.on('trackMuted', triggerRerender);
      participant.on('trackUnmuted', triggerRerender);

      return () => {
        participant.off('localTrackPublished', handlePublished);
        participant.off('localTrackUnpublished', handleUnpublished);
        participant.off('trackMuted', triggerRerender);
        participant.off('trackUnmuted', triggerRerender);
      };
    } else {
      // For remote participants, listen to trackSubscribed/trackUnsubscribed
      participant.on('trackSubscribed', handleTrack);
      participant.on('trackUnsubscribed', handleTrackDetach);

      // Listen for track mute/unmute events to update icons
      participant.on('trackMuted', triggerRerender);
      participant.on('trackUnmuted', triggerRerender);
      participant.on('trackPublished', triggerRerender);
      participant.on('trackUnpublished', triggerRerender);

      return () => {
        participant.off('trackSubscribed', handleTrack);
        participant.off('trackUnsubscribed', handleTrackDetach);
        participant.off('trackMuted', triggerRerender);
        participant.off('trackUnmuted', triggerRerender);
        participant.off('trackPublished', triggerRerender);
        participant.off('trackUnpublished', triggerRerender);
      };
    }
  }, [participant, isLocal]);

  // Listen for speaking state changes
  useEffect(() => {
    const handleSpeakingChanged = (speaking: boolean) => {
      console.log(`${participant.identity} speaking state changed:`, speaking);
      setIsSpeaking(speaking);
    };

    // Set initial state
    setIsSpeaking(participant.isSpeaking);

    // Listen for speaking changes
    participant.on('isSpeakingChanged', handleSpeakingChanged);

    return () => {
      participant.off('isSpeakingChanged', handleSpeakingChanged);
    };
  }, [participant]);

  // Additional effect to ensure UI updates when media states change
  // This is a fallback mechanism in case events don't fire properly
  useEffect(() => {
    const interval = setInterval(() => {
      // Force a re-render every 1 second to ensure media state icons are up to date
      triggerRerender();
      // Also sync speaking state
      if (participant.isSpeaking !== isSpeaking) {
        setIsSpeaking(participant.isSpeaking);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [participant.identity, participant.isSpeaking, isSpeaking]);

  // Get media states from participant
  const isCameraEnabled = participant.isCameraEnabled;
  const isMicrophoneEnabled = participant.isMicrophoneEnabled;
  const displayName = participant.name || participant.identity;

  // Compute the active video track: prefer screen share over camera
  const activeVideoTrack: VideoTrack | null = screenShareTrack || (participant.isCameraEnabled ? cameraTrack : null);

  // Attach the active video track whenever it changes
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Detach any previously attached tracks to avoid mixed streams
    try {
      if (cameraTrack) cameraTrack.detach(el);
      if (screenShareTrack) screenShareTrack.detach(el);
    } catch { }

    if (activeVideoTrack) {
      activeVideoTrack.attach(el);
      console.log('Active video track attached:', activeVideoTrack.source);
    } else {
      // Clear element when no active video
      try {
        // @ts-ignore - srcObject is writable on HTMLVideoElement
        el.srcObject = null;
      } catch { }
      console.log('No active video track, showing placeholder');
    }
  }, [activeVideoTrack, cameraTrack, screenShareTrack]);

  useEffect(() => {
    if (audioTrack && audioRef.current && !isLocal) {
      audioTrack.attach(audioRef.current);
      console.log('Audio track re-attached after ref update');
    }
  }, [audioTrack, isLocal]);

  // Apply audio output device (speaker) selection
  useEffect(() => {
    const applyAudioOutput = async () => {
      if (!audioRef.current || isLocal) return;

      // Check if setSinkId is supported (not available in all browsers)
      if (typeof (audioRef.current as any).setSinkId === 'function' && selectedAudioOutput) {
        try {
          await (audioRef.current as any).setSinkId(selectedAudioOutput);
          console.log('Audio output device set to:', selectedAudioOutput);
        } catch (error) {
          console.error('Failed to set audio output device:', error);
        }
      }
    };

    applyAudioOutput();
  }, [selectedAudioOutput, isLocal]);

  return (
    <div className="relative w-full h-full bg-black rounded-md overflow-hidden">
      {/* Video Element */}
      {activeVideoTrack && (
        <video
          ref={videoRef}
          className={cn(
            "absolute inset-0 w-full h-full",
            isFocused ? "object-contain" : "object-cover",
            // Only mirror camera video for local participant if isMirrored is enabled AND using front-facing camera
            activeVideoTrack.source !== Track.Source.ScreenShare && isLocal && isMirrored && facingMode === 'user' && "scale-x-[-1]"
          )}
          autoPlay
          playsInline
          muted={isLocal} // Mute local video to avoid feedback
        />
      )}

      {/* Audio Element (hidden) */}
      <audio ref={audioRef} autoPlay playsInline className="hidden" />

      {/* Avatar/Placeholder when no active video (no camera & no screen share) */}
      {!activeVideoTrack && (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-700">
          <User className="h-12 w-12 text-gray-400 opacity-50" />
        </div>
      )}

      {/* Participant Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="bg-gradient-to-t from-black/70 via-black/60 to-transparent backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center flex-1 min-w-0 mr-2">
            <span className="text-white text-sm font-medium truncate">
              {displayName}
              {isLocal && ' (You)'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isMicrophoneEnabled ? (
              <Mic className="h-4 w-4 text-white/90" strokeWidth={2.5} />
            ) : (
              <MicOff className="h-4 w-4 text-white/50" strokeWidth={2} />
            )}
            {isCameraEnabled ? (
              <Video className="h-4 w-4 text-white/90" strokeWidth={2.5} />
            ) : (
              <VideoOff className="h-4 w-4 text-white/50" strokeWidth={2} />
            )}
          </div>
        </div>
      </div>

      {/* Green Mic Indicator (top-left) */}
      {isMicrophoneEnabled && (
        <MicIndicator audioTrack={audioTrack} className="absolute top-4 left-4 z-10" />
      )}

      {/* Enlarge Button */}
      {onEnlarge && !isFocused && (
        <button
          onClick={onEnlarge}
          className="absolute top-4 right-4 h-8 px-2 py-1.5 bg-black/50 hover:bg-black/70 rounded-md transition-all duration-200 focus:opacity-100 flex items-center justify-center"
          aria-label="Enlarge participant view"
        >
          <Maximize2 className="h-4 w-4 text-white" />
        </button>
      )}

      {/* Speaking Indicator */}
      {isSpeaking && (
        <div className="absolute inset-0 border-4 border-green-500 rounded-lg pointer-events-none animate-pulse z-20" />
      )}
    </div>
  );
};

export default ParticipantTile;
